import { useState, useEffect, useRef } from "react";
import { useThemeContext } from "../theme/ThemeContext";
import { SA_AIRLINES, Button } from "./shared";
import { formatDateLabel } from "../utils/timeUtils";
import StatsBar from "./StatsBar";

export default function AppHeader({
  headerRef,
  saLogoData,
  appDate,
  isWide,
  isUA,
  myAirline,
  activeSCCO,
  onMenuOpen,
  stats,
  successRate,
  children,
}) {
  const { SA, GLASS, isDark, TYPOGRAPHY } = useThemeContext();

  const [spinning, setSpinning] = useState(false);
  const prevStatsKey = useRef(null);
  const spinTimer = useRef(null);

  useEffect(() => {
    const key = `${stats?.pax}|${stats?.exp}|${stats?.bags}|${successRate}`;
    if (prevStatsKey.current !== null && prevStatsKey.current !== key) {
      clearTimeout(spinTimer.current);
      setSpinning(true);
      spinTimer.current = setTimeout(() => setSpinning(false), 1400);
    }
    prevStatsKey.current = key;
  }, [stats, successRate]);

  const dateLabel = formatDateLabel(appDate);
  const dateLabelShort = dateLabel.replace(
    /(\w+)\s(\d+)\w+,\s(\d+)/,
    (_, m, d, y) => `${m.slice(0, 3)} ${d}, ${y}`
  );

  return (
    <div ref={headerRef} className="glass-grain" style={{
      ...GLASS.panel,
      position: "fixed", top: "calc(env(safe-area-inset-top, 0px) + 8px)", left: 8, right: 8, zIndex: 50,
      borderRadius: 20,
      overflow: "hidden",
      borderBottom: "none",
      border: `1px solid ${isDark ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.90)"}`,
      boxShadow: isDark
        ? "0 8px 32px rgba(0,0,0,0.50), 0 2px 8px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.10)"
        : "0 8px 24px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.90)",
    }}>
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>

        {/* Top row: logo + wordmark | date + SCCO + hamburger */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px" }}>

          {/* Left: logo + wordmark */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src={saLogoData} alt="Star Alliance" className={spinning ? "logo-spinning" : undefined} style={{
              width: 40, height: 40, objectFit: "contain", flexShrink: 0,
              mixBlendMode: isDark ? "screen" : "normal",
              filter: isDark ? "none" : "invert(1)",
              opacity: isDark ? 0.92 : 0.65,
            }} />
            <div>
              <div style={{
                fontSize: TYPOGRAPHY.note, color: SA.white, letterSpacing: "0.16em", fontWeight: 300,
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", lineHeight: 1.2,
              }}>
                LAX CONNECT
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.tiny, color: SA.silver, letterSpacing: "0.09em", marginTop: 3,
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
              }}>
                {isUA ? "United Airlines" : (SA_AIRLINES.find(a => a.code === myAirline)?.name || myAirline || "Partner")}
              </div>
            </div>
          </div>

          {/* Right: date + SCCO + hamburger */}
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ textAlign: "right" }}>
              <div style={{
                fontSize: TYPOGRAPHY.body, color: SA.lightSilver, letterSpacing: "0.06em",
                fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif", fontWeight: 300, lineHeight: 1.2,
              }}>
                {isWide ? dateLabel : dateLabelShort}
              </div>
              {isUA && activeSCCO && (
                <div style={{
                  fontSize: TYPOGRAPHY.tiny, color: SA.accent, marginTop: 3, fontWeight: 600,
                  letterSpacing: "0.04em", fontFamily: "'Barlow',sans-serif",
                }}>
                  {activeSCCO.name}
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              onClick={onMenuOpen}
              style={{ padding: "6px 2px", flexDirection: "column", gap: 5, flexShrink: 0, opacity: 0.7, transition: "opacity 0.15s" }}
              onMouseEnter={e => e.currentTarget.style.opacity = "1"}
              onMouseLeave={e => e.currentTarget.style.opacity = "0.7"}
            >
              {[0, 1, 2].map(i => (
                <div key={i} style={{ width: 20, height: 1.5, background: SA.lightSilver, borderRadius: 2 }} />
              ))}
            </Button>
          </div>
        </div>

        {/* Stats row */}
        <StatsBar stats={stats} successRate={successRate} />

      </div>

      {/* Filter toolbar slot — rendered by parent, measured as part of header height */}
      {children}
    </div>
  );
}
